import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import resourcesToBackend from "i18next-resources-to-backend";
import { DEFAULT_LOCALE } from "@huub-web/i18n";
import type { ThemeName } from "@huub-web/ui";

import { THEME_TO_BRAND } from "./config/brandLocales";
import { getAvailableLocales } from "./config/locales";

/**
 * Active build-time theme, used to gate which locales i18next may activate.
 * Defaults to "light" (Huub) when `VITE_THEME` is unset.
 */
const ACTIVE_THEME = (import.meta.env.VITE_THEME as ThemeName) || "light";

type BrandManifest = Record<string, Record<string, string[]>>;

/**
 * Load namespace manifest generated by sync-translations.mjs.
 * Falls back to ["common"] if the manifest cannot be fetched (e.g. first run
 * before the prebuild script has executed).
 */
async function loadNamespaceManifest(): Promise<string[]> {
  try {
    const res = await fetch("/locales/namespace-manifest.json");
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return (await res.json()) as string[];
  } catch {
    console.warn(
      "[i18n] Could not load namespace manifest, falling back to ['common']",
    );
    return ["common"];
  }
}

/**
 * Load brand manifest generated by sync-translations.mjs.
 * Returns an empty object when no brand overrides exist.
 */
async function loadBrandManifest(): Promise<BrandManifest> {
  try {
    const res = await fetch("/locales/brand-manifest.json");
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return (await res.json()) as BrandManifest;
  } catch {
    return {};
  }
}

/**
 * Apply brand translation overrides for all supported locales.
 *
 * Fetches brand-specific JSON files listed in the brand manifest and merges
 * them into the existing i18next resources via `addResourceBundle`. This keeps
 * `i18n.language` as a standard locale (e.g. "nl") while transparently
 * overriding specific keys for the active whitelabel brand.
 */
async function applyBrandOverlays({
  brandPrefix,
  locales,
  manifest,
  isStale,
}: {
  brandPrefix: string;
  locales: string[];
  /**
   * Pre-fetched brand manifest. When omitted the manifest is fetched internally.
   */
  manifest?: BrandManifest;
  /**
   * When provided, called before each `addResourceBundle`. If it returns `true`
   * the bundle is skipped, preventing a superseded call from overwriting the
   * translations that a newer call already applied.
   */
  isStale?: () => boolean;
}): Promise<void> {
  const resolvedManifest = manifest ?? (await loadBrandManifest());
  const brandConfig = resolvedManifest[brandPrefix];
  if (!brandConfig) return;

  const fetches: Promise<void>[] = [];

  for (const locale of locales) {
    const namespaces = brandConfig[locale];
    if (!namespaces) continue;

    for (const ns of namespaces) {
      fetches.push(
        fetch(`/locales/${brandPrefix}-${locale}/${ns}.json`)
          .then((res) => (res.ok ? res.json() : null))
          .then((data) => {
            if (data && !isStale?.()) {
              i18n.addResourceBundle(locale, ns, data, true, true);
            }
          })
          .catch(() => {}),
      );
    }
  }

  await Promise.all(fetches);
}

/**
 * Initialise i18next.
 *
 * Translations are fetched at runtime from /locales/<lng>/<ns>.json which is
 * populated by the sync-translations prebuild script into public/locales/.
 *
 * When `VITE_THEME` maps to a brand prefix (e.g. "verenigingEigenHuis" -> "veh"),
 * brand-specific translation overrides are merged on top of base translations
 * before the promise resolves, so the app always renders with brand copy.
 */
/**
 * Re-apply brand translation overlays for the given theme.
 *
 * Also realigns i18next's `supportedLngs` with the theme's available locales,
 * since the init value is frozen at the build-time ACTIVE_THEME and the active
 * theme can change at runtime (localStorage-persisted or debug-selected). When
 * the new theme no longer offers the active language it is demoted to the
 * default locale.
 *
 * Reloads base translations for every namespace that any brand could override
 * (clearing stale overrides), then merges the new brand's overrides on top.
 * Safe to call repeatedly — e.g. when the user switches themes at runtime.
 *
 * Uses a monotonically increasing nonce to guard against out-of-order
 * completion when called multiple times in rapid succession (e.g. A→B→C).
 * After every `await` the function checks whether a newer call has started
 * and bails out if so. The final `applyBrandOverlays` call additionally
 * receives an `isStale` callback that gates each `addResourceBundle` call,
 * preventing stale brand translations from overwriting the correct ones even
 * when the fetch-then-apply callbacks resolve after a newer call has finished.
 */
let switchNonce = 0;

async function switchWhitelabel({
  theme,
}: {
  theme: ThemeName;
}): Promise<void> {
  const nonce = ++switchNonce;

  const locales = getAvailableLocales(theme);

  // Realign i18next's accepted-language list with the active (runtime) theme
  // synchronously, before any await, so the switcher (getAvailableLocales) and
  // i18next never present a stale window to the user. `supportedLngs` is
  // otherwise frozen at init from the build-time ACTIVE_THEME, so a
  // localStorage-persisted or debug-selected theme that differs from
  // VITE_THEME would let the two drift apart — offering a locale i18next
  // blocks, or keeping one active that the switcher no longer lists. "cimode"
  // is preserved because i18next appends it at init for its key-debug mode.
  const supportedLngs = locales.includes("cimode")
    ? locales
    : [...locales, "cimode"];
  i18n.options.supportedLngs = supportedLngs;
  const languageUtils = i18n.services?.languageUtils as
    | { supportedLngs: readonly string[] | false }
    | undefined;
  if (languageUtils) {
    languageUtils.supportedLngs = supportedLngs;
  }

  // Demote the active language if the new theme no longer offers it (e.g.
  // switching away from Domek while Polish is active).
  if (!locales.includes(i18n.language)) {
    await i18n.changeLanguage(
      locales.includes(DEFAULT_LOCALE) ? DEFAULT_LOCALE : locales[0],
    );
    if (nonce !== switchNonce) return;
  }

  const manifest = await loadBrandManifest();
  if (nonce !== switchNonce) return;

  const affectedNamespaces = new Set<string>();
  for (const brandConfig of Object.values(manifest)) {
    for (const namespaces of Object.values(brandConfig)) {
      for (const ns of namespaces) {
        affectedNamespaces.add(ns);
      }
    }
  }

  const reloads: Promise<void>[] = [];
  for (const locale of locales) {
    for (const ns of affectedNamespaces) {
      reloads.push(
        fetch(`/locales/${locale}/${ns}.json`)
          .then((res) => (res.ok ? res.json() : null))
          .then((data) => {
            if (data && nonce === switchNonce) {
              i18n.addResourceBundle(locale, ns, data, false, true);
            }
          })
          .catch(() => {}),
      );
    }
  }
  await Promise.all(reloads);
  if (nonce !== switchNonce) return;

  const brandPrefix = THEME_TO_BRAND[theme] ?? null;
  if (brandPrefix) {
    await applyBrandOverlays({
      brandPrefix,
      locales,
      manifest,
      isStale: () => nonce !== switchNonce,
    });
  }
}

async function initI18n() {
  const namespaces = await loadNamespaceManifest();
  const supportedLocales = getAvailableLocales(ACTIVE_THEME);

  await i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .use(
      resourcesToBackend((lng: string, ns: string) =>
        fetch(`/locales/${lng}/${ns}.json`).then((res) => {
          if (!res.ok)
            throw new Error(`Failed to load /locales/${lng}/${ns}.json`);
          return res.json();
        }),
      ),
    )
    .init({
      supportedLngs: supportedLocales,
      fallbackLng: { pl: ["en", DEFAULT_LOCALE], default: [DEFAULT_LOCALE] },
      defaultNS: "common",
      ns: namespaces,
      fallbackNS: namespaces.filter((ns) => ns !== "common"),
      interpolation: {
        escapeValue: false, // React already escapes
      },
      detection: {
        order: ["querystring", "cookie"],
        lookupQuerystring: "language",
        lookupCookie: "huub_locale",
        caches: [],
      },
      showSupportNotice: false, // Disable i18next promotional console message
    });

  const brandPrefix = THEME_TO_BRAND[ACTIVE_THEME] ?? null;

  if (brandPrefix) {
    await applyBrandOverlays({ brandPrefix, locales: supportedLocales });
  }
}

const i18nReady = initI18n();

export { i18nReady, switchWhitelabel };
export default i18n;
